import type { InfiniteListBaseProps, RaRecord } from "ra-core";
import { InfiniteListBase } from "ra-core";

import { ListView, type ListViewProps } from "@/components/admin/list";

/**
 * A <List> variant that accumulates records as the user scrolls instead of
 * paginating. Same chrome as <List> (breadcrumb, title, actions, filters) —
 * only the controller differs: ra-core's InfiniteListBase concatenates all
 * loaded pages into ListContext and exposes fetchNextPage & friends through
 * InfinitePaginationContext.
 *
 * The pagination slot defaults to false: infinite loading UI belongs inside
 * the scroll container (an IntersectionObserver sentinel or a virtualized
 * list footer), not below it.
 *
 * @example
 * export const UserList = () => (
 *   <InfiniteList>
 *     <VirtualizedListContent itemContent={(_, user) => <UserRow user={user} />} />
 *   </InfiniteList>
 * );
 */
export const InfiniteList = <RecordType extends RaRecord = RaRecord>(
  props: InfiniteListProps<RecordType>,
) => {
  const {
    debounce,
    disableAuthentication,
    disableSyncWithLocation,
    exporter,
    filter,
    filterDefaultValues,
    loading,
    perPage,
    queryOptions,
    resource,
    sort,
    storeKey,
    ...rest
  } = props;

  return (
    <InfiniteListBase<RecordType>
      debounce={debounce}
      disableAuthentication={disableAuthentication}
      disableSyncWithLocation={disableSyncWithLocation}
      exporter={exporter}
      filter={filter}
      filterDefaultValues={filterDefaultValues}
      loading={loading}
      perPage={perPage}
      queryOptions={queryOptions}
      resource={resource}
      sort={sort}
      storeKey={storeKey}
    >
      <ListView<RecordType> pagination={false} {...rest} />
    </InfiniteListBase>
  );
};

export interface InfiniteListProps<RecordType extends RaRecord = RaRecord>
  extends Omit<InfiniteListBaseProps<RecordType>, "children" | "render">,
    ListViewProps<RecordType> {}
